import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Check, Car, Clock, FileText } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button, Card, cn } from '@rhpc/ui';
import { PageHeader, PageShell } from '@/components/PageShell';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SystemConfig {
  vehicleRateCar: number;
  vehicleRateMotorcycle: number;
  vehicleRateOther: number;
  officeAddress: string;
  maxClaimAmountMyr: number;
  submissionDeadlineDay: number;
  approvalReminderDays: number;
  allowedFileTypes: string[];
  maxFileSizeMb: number;
  hardcopyRemark?: string;
  documentClassificationDefault?: string;
}

interface AuditEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  userId: string | null;
  userEmail: string | null;
  before: string | null;
  after: string | null;
  createdAt: string;
}

const configKeys = {
  config: ['config'] as const,
  audit: ['config', 'audit'] as const,
};

// ---------------------------------------------------------------------------
// Audit badge styling
// ---------------------------------------------------------------------------

function auditBadgeClasses(action: string) {
  if (action.includes('APPROVED')) return 'bg-ok-soft text-ok';
  if (action.includes('REJECTED')) return 'bg-danger-soft text-danger';
  return 'bg-surface-2 text-text-3';
}

function AuditLogCard() {
  const { data, isLoading } = useQuery({
    queryKey: configKeys.audit,
    queryFn: () => api.get<{ logs: AuditEntry[] }>('/config/audit'),
  });

  const logs = data?.logs ?? [];

  return (
    <Card className="overflow-hidden">
      <div className="p-[20px_16px_16px] sm:p-[20px_32px_16px]">
        <h2 className="text-[15px] font-bold">Recent Changes</h2>
        <p className="mt-1 text-[13px] text-text-2">Audit log of configuration updates</p>
      </div>

      {isLoading ? (
        <div className="space-y-2 px-4 pb-6 sm:px-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-8 rounded" />
          ))}
        </div>
      ) : logs.length === 0 ? (
        <div className="border-t border-border p-6 text-center text-sm text-text-2">
          No changes recorded yet.
        </div>
      ) : (
        <div>
          {logs.slice(0, 10).map((entry) => (
            <div
              key={entry.id}
              className="flex items-center gap-3.5 border-t border-border px-4 py-[13px] sm:px-8 transition-colors hover:bg-surface-2/50"
            >
              <span className={cn('shrink-0 rounded-[5px] px-[9px] py-[3px] font-mono text-[11px] font-semibold', auditBadgeClasses(entry.action))}>
                {entry.action}
              </span>
              <span className="min-w-0 truncate text-[13px] text-text-2">
                by <span className="font-semibold text-text">{entry.userEmail ?? 'system'}</span>
              </span>
              <time className="ml-auto shrink-0 font-mono text-xs text-text-2">
                {new Date(entry.createdAt).toLocaleString('en-MY', {
                  timeZone: 'Asia/Kuala_Lumpur',
                  day: 'numeric', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </time>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------

function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="mb-[22px] flex items-center gap-2.5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface-2 text-text">
        {icon}
      </div>
      <h2 className="text-[15px] font-bold">{title}</h2>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Config Form
// ---------------------------------------------------------------------------

interface ConfigFormData {
  vehicleRateCar: number;
  vehicleRateMotorcycle: number;
  vehicleRateOther: number;
  officeAddress: string;
  maxClaimAmountMyr: number;
  submissionDeadlineDay: number;
  approvalReminderDays: number;
  allowedFileTypes: string;
  maxFileSizeMb: number;
  hardcopyRemark: string;
  documentClassificationDefault: string;
}

export function SystemConfigPage() {
  const queryClient = useQueryClient();
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: configKeys.config,
    queryFn: () => api.get<{ config: SystemConfig }>('/config'),
  });

  const config = data?.config;

  const { register, handleSubmit, reset, formState: { errors, isDirty, isSubmitting } } = useForm<ConfigFormData>({
    values: config
      ? {
          vehicleRateCar: config.vehicleRateCar,
          vehicleRateMotorcycle: config.vehicleRateMotorcycle,
          vehicleRateOther: config.vehicleRateOther,
          officeAddress: config.officeAddress,
          maxClaimAmountMyr: config.maxClaimAmountMyr,
          submissionDeadlineDay: config.submissionDeadlineDay,
          approvalReminderDays: config.approvalReminderDays,
          allowedFileTypes: config.allowedFileTypes.join(', '),
          maxFileSizeMb: config.maxFileSizeMb,
          hardcopyRemark: config.hardcopyRemark ?? 'Please ensure all original physical receipts and claim forms are printed, attached, and submitted to the Finance department.',
          documentClassificationDefault: config.documentClassificationDefault ?? 'CONFIDENTIAL',
        }
      : undefined,
  });

  const updateMutation = useMutation({
    mutationFn: (data: ConfigFormData) =>
      api.patch<{ config: SystemConfig; message: string }>('/config', {
        vehicleRateCar: Number(data.vehicleRateCar),
        vehicleRateMotorcycle: Number(data.vehicleRateMotorcycle),
        vehicleRateOther: Number(data.vehicleRateOther),
        officeAddress: data.officeAddress,
        maxClaimAmountMyr: Number(data.maxClaimAmountMyr),
        submissionDeadlineDay: Number(data.submissionDeadlineDay),
        approvalReminderDays: Number(data.approvalReminderDays),
        allowedFileTypes: data.allowedFileTypes.split(',').map((s) => s.trim()).filter(Boolean),
        maxFileSizeMb: Number(data.maxFileSizeMb),
        hardcopyRemark: data.hardcopyRemark,
        documentClassificationDefault: data.documentClassificationDefault,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: configKeys.config });
      queryClient.invalidateQueries({ queryKey: configKeys.audit });
      reset();
      setSuccessMsg(result.message ?? 'Configuration saved');
      setServerError(null);
      setTimeout(() => setSuccessMsg(null), 4000);
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : 'Failed to save configuration');
    },
  });

  const inputClass = 'h-10 w-full rounded-md border border-input bg-surface px-3 font-mono text-sm text-text focus:outline-2 focus:outline-primary focus:outline-offset-1';
  const textInputClass = 'h-10 w-full rounded-md border border-input bg-surface px-3 text-sm text-text focus:outline-2 focus:outline-primary focus:outline-offset-1';
  const labelClass = 'mb-1.5 block text-[13px] font-semibold';
  const hintClass = 'mt-1.5 text-xs text-text-2';

  return (
    <PageShell>
      <PageHeader
        title="System Configuration"
        description="Manage global settings for the RHPC Claim System. Changes take effect immediately."
      />

      {successMsg && (
        <div className="mb-5 flex items-center gap-2 rounded-md border border-ok/20 bg-ok-soft px-3 py-2.5" role="status">
          <Check className="h-4 w-4 text-ok" strokeWidth={2.4} />
          <p className="text-sm text-ok">{successMsg}</p>
        </div>
      )}

      {serverError && (
        <div className="mb-5 rounded-md border border-danger/20 bg-danger-soft p-3" role="alert">
          <p className="text-sm text-danger">{serverError}</p>
        </div>
      )}

      {isLoading ? (
        <Card className="space-y-4 p-[28px_32px]">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="space-y-1.5">
              <div className="skeleton h-4 w-32 rounded" />
              <div className="skeleton h-10 rounded-md" />
            </div>
          ))}
        </Card>
      ) : (
        <form onSubmit={handleSubmit((d) => updateMutation.mutate(d))} className="flex flex-col gap-5">
          {/* Mileage & office */}
          <Card className="p-[28px_32px]">
            <SectionHeader icon={<Car className="h-4 w-4" strokeWidth={2} />} title="Mileage Reimbursement Rates" />

            <div className="grid grid-cols-3 gap-5">
              <div>
                <label className={labelClass} htmlFor="vehicleRateCar">Car (RM/km)</label>
                <input
                  id="vehicleRateCar"
                  className={inputClass}
                  {...register('vehicleRateCar', { required: 'Required', min: { value: 0, message: '≥ 0' }, max: { value: 10, message: '≤ 10' }, valueAsNumber: true })}
                />
                {errors.vehicleRateCar && <p className="mt-1 text-xs text-danger">{errors.vehicleRateCar.message}</p>}
                <p className={hintClass}>Standard car rate</p>
              </div>
              <div>
                <label className={labelClass} htmlFor="vehicleRateMotorcycle">Motorcycle (RM/km)</label>
                <input
                  id="vehicleRateMotorcycle"
                  className={inputClass}
                  {...register('vehicleRateMotorcycle', { required: 'Required', min: { value: 0, message: '≥ 0' }, max: { value: 10, message: '≤ 10' }, valueAsNumber: true })}
                />
                {errors.vehicleRateMotorcycle && <p className="mt-1 text-xs text-danger">{errors.vehicleRateMotorcycle.message}</p>}
                <p className={hintClass}>Motorcycle rate</p>
              </div>
              <div>
                <label className={labelClass} htmlFor="vehicleRateOther">Other (RM/km)</label>
                <input
                  id="vehicleRateOther"
                  className={inputClass}
                  {...register('vehicleRateOther', { required: 'Required', min: { value: 0, message: '≥ 0' }, max: { value: 10, message: '≤ 10' }, valueAsNumber: true })}
                />
                {errors.vehicleRateOther && <p className="mt-1 text-xs text-danger">{errors.vehicleRateOther.message}</p>}
                <p className={hintClass}>Other vehicle types</p>
              </div>
            </div>

            <div className="my-[22px] h-px bg-border" />

            <div>
              <label className={labelClass} htmlFor="officeAddress">Company Office Address</label>
              <input
                id="officeAddress"
                className={textInputClass}
                {...register('officeAddress', { required: 'Required' })}
              />
              {errors.officeAddress && <p className="mt-1 text-xs text-danger">{errors.officeAddress.message}</p>}
              <p className={hintClass}>Used to calculate the home-office deduction for staff trips.</p>
            </div>
          </Card>

          {/* Claim limits & deadlines */}
          <Card className="p-[28px_32px]">
            <SectionHeader icon={<Clock className="h-4 w-4" strokeWidth={2} />} title="Claim Limits & Deadlines" />

            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className={labelClass} htmlFor="maxClaimAmountMyr">
                  Max Claim Amount (RM) <span className="text-danger">*</span>
                </label>
                <input
                  id="maxClaimAmountMyr"
                  className={inputClass}
                  {...register('maxClaimAmountMyr', { required: 'Required', min: { value: 0, message: 'Must be ≥ 0' }, valueAsNumber: true })}
                />
                {errors.maxClaimAmountMyr && <p className="mt-1 text-xs text-danger">{errors.maxClaimAmountMyr.message}</p>}
                <p className={hintClass}>Maximum total claim amount per submission</p>
              </div>
              <div>
                <label className={labelClass} htmlFor="submissionDeadlineDay">
                  Submission Deadline (day of month) <span className="text-danger">*</span>
                </label>
                <input
                  id="submissionDeadlineDay"
                  className={inputClass}
                  {...register('submissionDeadlineDay', { required: 'Required', min: { value: 1, message: 'Min 1' }, max: { value: 28, message: 'Max 28' }, valueAsNumber: true })}
                />
                {errors.submissionDeadlineDay && <p className="mt-1 text-xs text-danger">{errors.submissionDeadlineDay.message}</p>}
                <p className={hintClass}>Claims submitted after this day are for the next cycle</p>
              </div>
              <div>
                <label className={labelClass} htmlFor="approvalReminderDays">
                  Approval Reminder (days) <span className="text-danger">*</span>
                </label>
                <input
                  id="approvalReminderDays"
                  className={inputClass}
                  {...register('approvalReminderDays', { required: 'Required', min: { value: 1, message: 'Min 1' }, max: { value: 30, message: 'Max 30' }, valueAsNumber: true })}
                />
                {errors.approvalReminderDays && <p className="mt-1 text-xs text-danger">{errors.approvalReminderDays.message}</p>}
                <p className={hintClass}>Sent to approvers this many days before deadline</p>
              </div>
            </div>
          </Card>

          {/* File upload rules */}
          <Card className="p-[28px_32px]">
            <SectionHeader icon={<FileText className="h-4 w-4" strokeWidth={2} />} title="File Upload Rules" />

            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className={labelClass} htmlFor="allowedFileTypes">
                  Allowed File Types <span className="text-danger">*</span>
                </label>
                <input
                  id="allowedFileTypes"
                  className={inputClass}
                  {...register('allowedFileTypes', { required: 'Required' })}
                />
                {errors.allowedFileTypes && <p className="mt-1 text-xs text-danger">{errors.allowedFileTypes.message}</p>}
                <p className={hintClass}>Comma-separated MIME types</p>
              </div>
              <div>
                <label className={labelClass} htmlFor="maxFileSizeMb">
                  Max File Size (MB) <span className="text-danger">*</span>
                </label>
                <input
                  id="maxFileSizeMb"
                  className={inputClass}
                  {...register('maxFileSizeMb', { required: 'Required', min: { value: 1, message: 'Min 1MB' }, max: { value: 50, message: 'Max 50MB' }, valueAsNumber: true })}
                />
                {errors.maxFileSizeMb && <p className="mt-1 text-xs text-danger">{errors.maxFileSizeMb.message}</p>}
                <p className={hintClass}>Maximum size per uploaded receipt file</p>
              </div>
            </div>
          </Card>

          {/* Compliance & Process Reminders */}
          <Card className="p-[28px_32px]">
            <SectionHeader icon={<FileText className="h-4 w-4" strokeWidth={2} />} title="Process Reminders & Document Classification" />

            <div className="space-y-5">
              <div>
                <label className={labelClass} htmlFor="hardcopyRemark">
                  Physical Hardcopy Submission Remark <span className="text-danger">*</span>
                </label>
                <textarea
                  id="hardcopyRemark"
                  rows={3}
                  className="w-full rounded-md border border-input bg-surface p-3 text-sm text-text focus:outline-2 focus:outline-primary focus:outline-offset-1 leading-relaxed"
                  {...register('hardcopyRemark', { required: 'Required' })}
                />
                {errors.hardcopyRemark && <p className="mt-1 text-xs text-danger">{errors.hardcopyRemark.message}</p>}
                <p className={hintClass}>Displayed in the submission confirmation popup to remind staff to prepare original hardcopies</p>
              </div>

              <div>
                <label className={labelClass} htmlFor="documentClassificationDefault">
                  Document Classification Header Label <span className="text-danger">*</span>
                </label>
                <input
                  id="documentClassificationDefault"
                  className={textInputClass}
                  {...register('documentClassificationDefault', { required: 'Required' })}
                />
                {errors.documentClassificationDefault && <p className="mt-1 text-xs text-danger">{errors.documentClassificationDefault.message}</p>}
                <p className={hintClass}>Classification banner printed on all generated Excel export sheets (e.g. CONFIDENTIAL)</p>
              </div>
            </div>

            <div className="mt-[26px] flex justify-end gap-2.5 border-t border-border pt-[22px]">
              <Button type="button" variant="outline" onClick={() => reset()} disabled={!isDirty}>
                Discard Changes
              </Button>
              <Button type="submit" disabled={!isDirty} loading={isSubmitting || updateMutation.isPending}>
                Save Configuration
              </Button>
            </div>
          </Card>
        </form>
      )}

      <div className="mt-5">
        <AuditLogCard />
      </div>
    </PageShell>
  );
}
